<template>
  <b-modal v-model="showModal" :title="modalTitle" :size="modalSize" hide-footer @shown="fetchServices" @hidden="handleHide">
    <div v-if="servicesLoading" class="text-center py-3">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <div v-else style="max-height: 80vh; overflow-y: auto; position: relative; padding-bottom: 20px;">
      <div v-for="service in services" :key="service.id" class="mb-3 p-2 mx-3 card-body badge-glow border-secondary border rounded">
        <h6 class="mb-2 text-success font-weight-bold font-italic">{{ service.name }}</h6>
        <div class="d-flex align-items-center mb-2">
          <div class="custom-control custom-radio mr-3">
            <input
              type="radio"
              :id="`enable-${service.id}`"
              :name="`service-${service.id}`"
              class="custom-control-input"
              :value="true"
              v-model="serviceConfig[service.id]"
            />
            <label class="custom-control-label" :for="`enable-${service.id}`">Enable</label>
          </div>
          <div class="custom-control custom-radio mr-3">
            <input
              type="radio"
              :id="`disable-${service.id}`"
              :name="`service-${service.id}`"
              class="custom-control-input"
              :value="false"
              v-model="serviceConfig[service.id]"
            />
            <label class="custom-control-label" :for="`disable-${service.id}`">Disable</label>
          </div>
        </div>
        <!-- Dynamic Configuration Fields -->
        <div v-if="service.configurations && service.configurations[configurationType]" class="mt-2 pt-2">
          <p class="font-weight-bold mb-2 text-center text-muted">Other Fields</p>
          <div v-for="config in service.configurations[configurationType]" :key="config.name" class="mb-2">
            <label v-if="config.type !== 'checkbox'" :for="`config-${service.id}-${config.name}`" class="form-label">
              {{ config.title }}
              <span v-if="config.required" class="text-danger">*</span>
            </label>
            <!-- Text Input -->
            <input
              v-if="config.type === 'text'"
              :id="`config-${service.id}-${config.name}`"
              type="text"
              class="form-control form-control-sm"
              v-model="(serviceConfigurationValues[service.id] || {})[config.name]"
              :placeholder="config.title"
              :required="config.required"
            />
            <!-- Number Input -->
            <input
              v-if="config.type === 'number'"
              :id="`config-${service.id}-${config.name}`"
              type="number"
              class="form-control form-control-sm"
              v-model="(serviceConfigurationValues[service.id] || {})[config.name]"
              :placeholder="config.title"
              :required="config.required"
            />
            <!-- Dropdown -->
            <select
              v-if="config.type === 'dropDown'"
              :id="`config-${service.id}-${config.name}`"
              class="form-control form-control-sm"
              v-model="(serviceConfigurationValues[service.id] || {})[config.name]"
              :required="config.required"
            >
              <option value="">Select...</option>
              <option v-for="option in config.options" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
            <!-- Multiple Dropdown -->
            <multiselect
              v-if="config.type === 'multipleDropDown'"
              v-model="(serviceConfigurationValues[service.id] || {})[config.name]"
              :options="config.options"
              :multiple="true"
              class="shorten-multiselect-option"
              :close-on-select="false"
              :clear-on-select="false"
              :placeholder="'Select ' + config.title"
              :name="config.name"
            ></multiselect>
            <!-- Checkbox -->
            <div class="custom-control custom-switch mb-1" v-if="config.type === 'checkbox'">
              <input
                type="checkbox"
                class="custom-control-input"
                :id="`config-${service.id}-${config.name}`"
                v-model="(serviceConfigurationValues[service.id] || {})[config.name]"
              />
              <label class="custom-control-label" :for="`config-${service.id}-${config.name}`">
                {{ config.title }}
                <span v-if="config.required" class="text-danger">*</span>
              </label>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-end mt-2">
          <b-button
            type="button"
            variant="success"
            class="shake-me"
            size="sm"
            @click="saveServiceConfig(service.id)"
            :disabled="serviceSaving[service.id]"
          >
            <span v-if="serviceSaving[service.id]">Saving...</span>
            <span v-else>Save</span>
          </b-button>
        </div>
      </div>
<!--      <div class="d-flex justify-content-end mt-2">-->
<!--        <b-button type="button" variant="outline-secondary" @click="handleHide">-->
<!--          Close-->
<!--        </b-button>-->
<!--      </div>-->
    </div>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  props: {
    show: {
      type: Boolean,
      default: false
    },
    row: {
      type: Object,
      default: null
    },
    type: {
      type: String,
      default: 'store' // 'store', 'channel', or 'merchant'
    },
    configurationType: {
      type: String,
      default: 'storeConfigurations' // 'merchantConfigurations', 'storeConfigurations', or 'channelConfigurations'
    },
    modalSize: {
      type: String,
      default: 'md' // 'sm', 'md', 'lg', 'xl'
    },
    modalTitle: {
      type: String,
      default: 'Service Configuration'
    }
  },
  data() {
    return {
      services: [],
      servicesLoading: false,
      serviceConfig: {},
      serviceSaving: {},
      servicesFetched: false,
      serviceConfigurationValues: {}
    };
  },
  components: {
    Multiselect
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.handleHide();
        }
      }
    }
  },
  methods: {
    handleHide() {
      this.$emit('hide');
    },
    async fetchServices() {
      // Only fetch services once globally
      if (this.servicesFetched) {
        this.initializeServiceConfig();
        return;
      }

      this.servicesLoading = true;
      this.services = [];
      this.serviceConfig = {};

      this.makeRequest("post", "v1/get-charges-types-data", { services: 1 },{}, false).then(
        (callResponse) => {
          this.servicesLoading = false;
          if (!callResponse.error) {
            let code = callResponse.response.data.code;
            if (code == 200) {
              let data = callResponse.response.data.data;
              this.services = data.services || [];
              this.servicesFetched = true;
              this.initializeServiceConfig();
            }
          }
        }
      );
    },
    initializeServiceConfig() {
      // Reset service config before initializing
      this.serviceConfig = {};
      this.serviceConfigurationValues = {};
      
      // Initialize service config based on existing service_configuration
      if (this.row && this.row.service_configuration) {
        this.services.forEach(service => {
          const existingConfig = this.row.service_configuration.find(
            sc => sc.service_id === service.id
          );
          // Only set value if matching object exists, otherwise leave undefined (no radio selected)
          if (existingConfig) {
            // Convert integer status (1/0) to boolean for radio buttons
            this.serviceConfig[service.id] = existingConfig.status === 1;
            // Initialize configuration values if they exist
            if (existingConfig.configurations) {
              if (typeof existingConfig.configurations === 'string') {
                try {
                  this.$set(this.serviceConfigurationValues, service.id, JSON.parse(existingConfig.configurations));
                } catch (e) {
                  this.$set(this.serviceConfigurationValues, service.id, {});
                }
              } else if (typeof existingConfig.configurations === 'object') {
                this.$set(this.serviceConfigurationValues, service.id, existingConfig.configurations);
              } else {
                this.$set(this.serviceConfigurationValues, service.id, {});
              }
            } else {
              this.$set(this.serviceConfigurationValues, service.id, {});
            }
          } else {
            // Initialize empty configuration values
            this.$set(this.serviceConfigurationValues, service.id, {});
          }
          // Initialize field defaults based on service configuration schema
          if (service.configurations && service.configurations[this.configurationType]) {
            service.configurations[this.configurationType].forEach(config => {
              if (this.serviceConfigurationValues[service.id][config.name] === undefined) {
                if (config.type === 'checkbox') {
                  this.$set(this.serviceConfigurationValues[service.id], config.name, false);
                } else if (config.type === 'multipleDropDown') {
                  this.$set(this.serviceConfigurationValues[service.id], config.name, []);
                } else {
                  this.$set(this.serviceConfigurationValues[service.id], config.name, '');
                }
              }
            });
          }
        });
      } else {
        // Initialize empty configuration values for all services
        this.services.forEach(service => {
          this.$set(this.serviceConfigurationValues, service.id, {});
          // Initialize field defaults based on service configuration schema
          if (service.configurations && service.configurations[this.configurationType]) {
            service.configurations[this.configurationType].forEach(config => {
              if (config.type === 'checkbox') {
                this.$set(this.serviceConfigurationValues[service.id], config.name, false);
              } else if (config.type === 'multipleDropDown') {
                this.$set(this.serviceConfigurationValues[service.id], config.name, []);
              } else {
                this.$set(this.serviceConfigurationValues[service.id], config.name, '');
              }
            });
          }
        });
      }
    },
    async saveServiceConfig(serviceId) {
      this.$set(this.serviceSaving, serviceId, true);
      let data = {
        serviceId: serviceId,
        type: this.type,
        typeId: this.row.id,
        status: this.serviceConfig[serviceId] ? 1 : 0
      };

      // Only include configurations if the service has configuration fields for the given type
      const service = this.services.find(s => s.id === serviceId);
      if (service && service.configurations && service.configurations[this.configurationType]) {
        data.configurations = JSON.stringify(this.serviceConfigurationValues[serviceId] || {});
      }

      this.makeRequest("post", "v1/service-configuration", data).then(
        (callResponse) => {
          this.$set(this.serviceSaving, serviceId, false);
          if (!callResponse.error) {
            this.$emit('completed');
          }
        }
      );
    }
  }
};
</script>
<style scoped>
.shake-me {
  position: relative;
  overflow: hidden; /* Crucial: keeps the light flash inside the button borders */
}

/* This creates the glossy light streak */
.shake-me::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 60%; /* Slightly wider streak for better visibility */
  height: 100%;
  /* Bright white gradient streak */
  background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.6) 50%,
      rgba(255, 255, 255, 0) 100%
  );
  transform: skewX(-25deg); /* Tilts the light line */
  /* Runs a quick 2-second cycle infinitely */
  animation: shineStreak 2s ease-in-out infinite;
}

@keyframes shineStreak {
  0% {
    left: -150%;
  }
  30% {
    left: 150%; /* Slides across quickly in the first 0.6 seconds */
  }
  100% {
    left: 150%; /* Pauses on the right side for the remaining 1.4 seconds */
  }
}
</style>
